import React from "react";
import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
    Row,
    Col,
    OverlayTrigger,
    Tooltip,
    Button
} from "react-bootstrap";
import { logout } from "../../../utils/index.js";

import { ThreeCircles, Bars } from "react-loader-spinner";
import * as Icon from "react-bootstrap-icons";
import '../EngagementLetter.css';
import './MyTemplatesPage.css';
import { toast } from "react-toastify";
import CreateOrEditEditorComponent from '../CreateOrEditEditorComponent/CreateOrEditEditorComponent';
import { v4 as uuidv4 } from 'uuid';
import TemplateSettingModal from "../TemplateSettingModal/TemplateSettingModal";
import TemplatePreviewModal from '../template_preview_modal/TemplatePreviewModal.js';
import { TimeRenderer } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";

const MyTemplatesPage = (props) => {
    const [rowHeight, setRowHeight] = useState(25);
    const [refreshGrid, setRefreshGrid] = useState(false);
    const [engagementTemplateDetails, setEngagementTemplateDetails] = useState([]);
    const [isRowSelected, setIsRowSelected] = useState(false);
    const [createOrEditEditorComponentShow, setCreateOrEditEditorComponentShow] = useState(false);
    const [templateCreation, setTemplateCreation] = useState(false);
    const [docFileName, setDocFileName] = useState("");
    const [templateSettingModal, setTemplateSettingModal] = useState(false);
    const [templatePreviewModal, setTemplatePreviewModal] = useState(false);
    const [pdfFileUrl, setPdfFileUrl] = useState();
    const [userName, setUserName] = useState(localStorage.getItem("username"));
    const [templateData, setTemplateData] = useState({});
    const [gridKey, setGridKey] = useState(0);

    const toastBasicConfig = {
        position: "bottom-right",
        autoClose: 2500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
    };

    const toastErrorConfig = {
        ...toastBasicConfig,
        className: "toastify-color-error",
    };

    const fetchEngagementTemplatePageData = useCallback(() => {
        // Reset state
        resetState();

        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
            }
        };

        setRefreshGrid(true);

        fetch(`${process.env.REACT_APP_BASE_URI}/mdm/engagement-letter/templates/`, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401) {
                        // Handle unauthorized error
                        logout();
                        alert("Session ended , Please login back");
                    } else {
                        throw new Error("Request failed.");
                    }
                }
                return response.json();
            })
            .then((data) => {
                setEngagementTemplateDetails(data.data);
            })
            .finally(() => {
                setRefreshGrid(false);
            });
    }, []);

    // Reference to the AgGrid component
    const gridRef = useRef();

    // Style for the AgGrid
    const gridStyle = useMemo(() => ({ height: "85vh", width: "100%" }), []);

    // Callback to handle sort change
    const onSortChanged = (e) => {
        e.api.refreshCells();
    };

    const removeFileExtension = (filename) => {
        return filename.replace(/\.[^/.]+$/, "");
    };

    /**
     * Button cell renderer for the grid.
     * @param {Object} props - Props for the button cell renderer.
     * @returns Button for editing the row in the grid.
     */
    const EditBtnCellRenderer = (props) => {
        const getEngagementLetter = useCallback(async (fileName, containerName) => {
            const getRequestOptions = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                }
            };

            return await fetch(`${process.env.REACT_APP_BASE_URI}/mdm/download/blob/?FileName=${encodeURIComponent(fileName)}&ContainerName=${containerName}`, getRequestOptions)
                .then((response) => {
                    if (!response.ok) {
                        if (response.status === 401) {
                            // Handle unauthorized error
                            logout();
                            alert("Session ended , Please login back");
                        } else {
                            throw new Error("Request failed.");
                        }
                    }
                    return response.blob();
                }).then((blob) => {
                    return blob;
                })
        }, []);

        const viewClick = async () => {
            const filename = props.data.FileName;
            const filenameWithoutExtension = filename.substring(0, filename.lastIndexOf('.')) || filename;
            const pdfFilePath = filenameWithoutExtension.replace("DOCX", "PDF") + ".pdf";

            const blob = await getEngagementLetter(pdfFilePath, "templatefiles");
            const url = window.URL.createObjectURL(blob);

            setPdfFileUrl(url);
            setTemplatePreviewModal(true);
        }

        const editClick = () => {
            const filename = props.data.FileName;
            const filenameWithoutExtension = filename.substring(0, filename.lastIndexOf('.')) || filename;
            localStorage.setItem("key", filenameWithoutExtension);

            setDocFileName(props.data.FileName);

            localStorage.setItem('doc_original_file_name', props.data.TemplateOriginalFileName);

            localStorage.setItem('letter_file_name', filenameWithoutExtension + ".pdf");

            localStorage.setItem('signatureFields', props.data.FrontendSignatureDataList);
            localStorage.setItem('recipients', props.data.Recipients);
            localStorage.setItem('template_id', props.data.TemplateId);
            localStorage.setItem('isEdit', JSON.stringify(true));
            localStorage.setItem('edit_type', "Template");

            setCreateOrEditEditorComponentShow(true);
            setTemplateData(props.data)
        }

        const settingBtnClick = () => {
            localStorage.setItem('template_id', props.data.TemplateId);
            const filename = props.data.FileName;
            const filenameWithoutExtension = filename.substring(0, filename.lastIndexOf('.')) || filename;
            localStorage.setItem("key", filenameWithoutExtension);
            
            setTemplateSettingModal(true);
            setTemplateData(props.data);
        }

        const enable_buttons = ["Ready"].includes(props.data.Status);

        return (
            <div>
                <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                        <Tooltip id="button-tooltip">
                            {enable_buttons
                                ? "Edit template"
                                : "Template not ready"
                            }
                        </Tooltip>
                    }
                    error={true}
                >
                    <div className="tooltipWrapper" style={{
                        cursor: enable_buttons ? "pointer" : "not-allowed",
                    }}>
                        <Button
                            className="editActionButton actionButton"
                            onClick={() => editClick()}
                            disabled={!enable_buttons}
                            style={{
                                pointerEvents: (
                                    enable_buttons ? "all" : "none"
                                ),
                            }}>
                            <Icon.PencilSquare />
                        </Button>
                    </div>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                        <Tooltip id="button-tooltip">
                            {enable_buttons
                                ? "View template"
                                : "Template not ready"
                            }
                        </Tooltip>
                    }
                    error={true}
                >
                    <span className="tooltipWrapper" style={{
                        cursor: enable_buttons ? "pointer" : "not-allowed",
                    }}>
                        <Button
                            className="actionButton"
                            onClick={() => viewClick()}
                            disabled={!enable_buttons}
                            style={{
                                pointerEvents: (
                                    enable_buttons ? "all" : "none"
                                ),
                            }}
                        >
                            <Icon.Eye />
                        </Button>
                    </span>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                        <Tooltip id="button-tooltip">
                            Configure template settings
                        </Tooltip>
                    }
                    error={true}
                >
                    <div className="tooltipWrapper">
                        <Button
                            className="actionButton"
                            onClick={() => settingBtnClick()}>
                            <Icon.Gear />
                        </Button>
                    </div>
                </OverlayTrigger>
            </div>
        );
    }

    const TemplateFileNameRenderer = (file_name_props) => {
        const template_id = file_name_props.data.TemplateId;

        const downloadTemplate = useCallback((template_id, file_name) => {
            const getRequestOptions = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                }
            };

            fetch(`${process.env.REACT_APP_BASE_URI}/mdm/engagement-letter/download/template/?TemplateId=${template_id}`, getRequestOptions)
                .then((response) => {
                    if (!response.ok) {
                        if (response.status === 401) {
                            // Handle unauthorized error
                            logout();
                            alert("Session ended , Please login back");
                        } else {
                            throw new Error("Request failed.");
                        }
                    }
                    return response.blob();
                }).then((blob) => {
                    const href = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = href;
                    link.setAttribute('download', file_name);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
        }, []);

        return (
            <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 400 }}
                overlay={
                    <Tooltip id="button-tooltip">
                        Click to download
                    </Tooltip>
                }
                error={true}
            >
                <div style={{ padding: 0, margin: 0 }}>
                    <a className="icon-button" style={{ padding: 0, justifyContent: "left" }} onClick={() => downloadTemplate(template_id, file_name_props.data["TemplateOriginalFileName"])}>
                        <span className="download-links">
                            {removeFileExtension(file_name_props.data["TemplateOriginalFileName"])}
                        </span>
                    </a>
                </div>
            </OverlayTrigger>
        )
    }

    const FileNameHeader = ({ headerName, IconComponent }) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <IconComponent style={{ marginRight: '5px' }} />
                <span>{headerName}</span>
            </div>
        );
    };


    const defaultTemplateDetailsColumnDefs = [
        {
            headerName: "Template Name",
            field: "TemplateOriginalFileName",
            flex: 2,
            cellRenderer: TemplateFileNameRenderer,
            headerComponent: FileNameHeader,
            headerComponentParams: {
                headerName: "Template Name",
                IconComponent: Icon.FileEarmarkWordFill
            },
            sortable: false,
            checkboxSelection: true
        },
        {
            headerName: "Status",
            field: "Status",
            flex: 1,
            sortable: false
        },
        {
            headerName: "Updated By",
            field: "UpdatedBy",
            flex: 1,
            sortable: false
        },
        {
            headerName: "Created Date",
            field: "CreatedDate",
            flex: 1,
            cellRenderer: TimeRenderer,
            cellRendererParams: (params) => ({
                value: params.value,
            }),
            sortable: false,
        },
        {
            headerName: "Updated Date",
            field: "UpdatedDate",
            flex: 1,
            cellRenderer: TimeRenderer,
            cellRendererParams: (params) => ({
                value: params.value,
            }),
            sortable: false,
        },
        {
            headerName: "Action",
            field: "",
            cellRenderer: EditBtnCellRenderer,
            pinned: "right",
            maxWidth: 100,
            minWidth: 100,
            filter: false,
            sortable: false
        }
    ];

    // Column definitions for AgGrid
    const [templateDetailsColumnDefs, setTemplateDetailsColumnDefs] = useState(defaultTemplateDetailsColumnDefs);

    useEffect(() => {
        fetchEngagementTemplatePageData();
    }, []);

    // Default column definition for AgGrid
    const defaultColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
            animateRows: true,
        }),
        []
    );

    let gridApi;
    // Callback when the grid is ready
    const onGridReady = useCallback((params) => {
        gridApi = params.api;
        gridApi.closeToolPanel();
    }, []);

    /**
     * Custom overlay component for no rows.
     * @param {Object} props - Props for the overlay component.
     * @returns Overlay component for displaying a message when no rows are present.
     */
    const CustomNoRowsOverlay = (props) => {
        return (
            <div
                className="ag-overlay-loading-center flex-row-center-center"
                style={{ height: '9%' }}
            >
                <div className="spinner flex-column-center-center" style={{ fontSize: 14, fontWeight: 500 }}>
                    {props.noRowsMessageFunc()}
                </div>
            </div>
        );
    };

    const resetState = () => {
        setRefreshGrid(false);
        setEngagementTemplateDetails([]);
        setTemplateDetailsColumnDefs(defaultTemplateDetailsColumnDefs);
        setIsRowSelected(false);
    };

    // Function to handle row selection change
    const onSelectionChanged = useCallback(async (event) => {
        const selectedRows = event.api.getSelectedRows();
        setIsRowSelected(selectedRows.length > 0); // Update state based on whether rows are selected

        if (selectedRows.length > 0) {
            const templateId = selectedRows[0]["TemplateId"];
            console.log("templateId", templateId)
            localStorage.setItem("template_id", JSON.stringify(templateId));
        }
    }, []);

    const fetchTemplateApprovalConfigDetails = (templateId) => {
        return new Promise((resolve, reject) => {
            let token = localStorage.getItem("token");
            const getRequestOptions = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            };

            if (templateId !== undefined) {
                fetch(
                    `${process.env.REACT_APP_BASE_URI}/mdm/engagement-letter/template_approval_configuration_details/?TemplateId=${templateId}`,
                    getRequestOptions
                )
                    .then((response) => {
                        if (!response.ok) {
                            if (response.status === 401) {
                                logout();
                                alert("Session ended , Please login back");
                            } else {
                                reject(new Error("Request failed."));
                            }
                        }
                        return response.json();
                    })
                    .then((result) => {
                        resolve(result["data"]);
                    })
                    .catch((error) => reject(error));
            } else {
                reject(new Error("Template ID is undefined"));
            }
        });
    };

    const [selectedFieldOptions, setSelectedFieldOptions] = useState({})
    useEffect(() => {
        const token = localStorage.getItem("token")
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        fetch(`${process.env.REACT_APP_BASE_URI}/mdm/engagement-letter/firm_template_fields/`, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401) {
                        // Handle unauthorized error
                        logout();
                        alert("Session ended , Please login back");
                    } else {
                        throw new Error("Request failed.");
                    }
                }
                return response.json();
            })
            .then((data) => {
                if (data.success === true) {
                    console.log("SELECTED FIELD OPTIONS: ", JSON.parse(data["data"][0]["SelectedFields"]))
                    setSelectedFieldOptions(JSON.parse(data["data"][0]["SelectedFields"]))
                } else {
                    toast.error(
                        "Failed to fetch selected fields: " + data.message,
                        {
                            position: "bottom-right",
                            autoClose: 2500,
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            className: "toastify-color-error",
                        }
                    );
                }
            })
            .catch((err) => console.log("Err: ", err));
    }, [])

    const createTemplateBtnClick = () => {
        const newUUID = uuidv4();
        localStorage.setItem("key", newUUID);
        console.log(`${newUUID}`);

        setDocFileName("EngagementLetter.docx");
        localStorage.setItem('letter_file_name', "EngagementLetter.pdf");

        localStorage.setItem('signatureFields', JSON.stringify({}));
        localStorage.setItem('recipients', JSON.stringify([]));

        localStorage.setItem('isEdit', JSON.stringify(false));

        localStorage.setItem("template_id", JSON.stringify(-1));

        localStorage.setItem('edit_type', "Template");

        setCreateOrEditEditorComponentShow(true);
    }

    // JSX for the page component
    return (
        <>
            <>
                {createOrEditEditorComponentShow && (
                    <CreateOrEditEditorComponent
                        templateId={localStorage.getItem("template_id")}
                        docfilename={docFileName}
                        pdffilename={localStorage.getItem("letter_file_name")}
                        show={createOrEditEditorComponentShow}
                        onHide={() => {
                            setCreateOrEditEditorComponentShow(false);
                            if (localStorage.getItem("template_id") !== "-1") {
                                // setTemplateSettingModal(true);
                            }
                            else {
                                fetchEngagementTemplatePageData();
                            }
                        }}
                        setTemplateCreation={(flag) => setTemplateCreation(flag)}
                        edittype={"Template"}
                    />
                )}
                {templateSettingModal && (
                    <TemplateSettingModal
                        show={templateSettingModal}
                        templateId={localStorage.getItem("template_id")}
                        templateData={templateData}
                        onHide={(didSave) => {
                            setTemplateSettingModal(false);

                            if(didSave)
                                fetchEngagementTemplatePageData();
                        }}
                    />
                )}
                {templatePreviewModal && (
                    <TemplatePreviewModal
                        show={templatePreviewModal}
                        pdfFileUrl={pdfFileUrl}
                        onHide={() => setTemplatePreviewModal(false)}
                    />
                )}

                {!createOrEditEditorComponentShow && (
                    <>
                        <Row className='no-gutter'>
                            <p className="display-6" style={{ height: "fit-content", width: "fit-content", padding: 0, marginBottom: 0 }}>
                                Engagement Letter templates
                            </p>
                        </Row>
                        <Row className='no-gutter'>
                            <Col md={12} className="d-flex justify-content-end" style={{ float: "right" }}>
                                <div style={{ marginBottom: "0rem", marginRight: "1rem", marginTop: "-2.1rem" }}>
                                    <Button
                                        onClick={createTemplateBtnClick}
                                        className="custom-button"
                                    >
                                        {
                                            templateCreation ?
                                                <div style={{ marginRight: 6 }}>
                                                    <ThreeCircles
                                                        height="15"
                                                        width="15"
                                                        color="#4fa94d"
                                                        wrapperStyle={{}}
                                                        wrapperClass=""
                                                        visible={true}
                                                        ariaLabel="three-circles-rotating"
                                                        outerCircleColor="var(--theme1_tertiary)"
                                                        innerCircleColor="var(--theme1_secondary)"
                                                        middleCircleColor="var(--theme1_primary)"
                                                    />
                                                </div>
                                                :
                                                null
                                        }
                                        <p style={{ marginBottom: 0, marginTop: 0 }}>Create Template</p>
                                    </Button>
                                </div>
                                <div style={{ marginBottom: "0rem", marginTop: "-1.75rem" }}>
                                    {
                                        refreshGrid ?
                                            <div>
                                                <ThreeCircles
                                                    height="20"
                                                    width="20"
                                                    color="#4fa94d"
                                                    wrapperStyle={{}}
                                                    wrapperClass=""
                                                    visible={true}
                                                    ariaLabel="three-circles-rotating"
                                                    outerCircleColor="var(--theme1_tertiary)"
                                                    innerCircleColor="var(--theme1_secondary)"
                                                    middleCircleColor="var(--theme1_primary)"
                                                />
                                            </div>
                                            :
                                            <Icon.ArrowCounterclockwise onClick={fetchEngagementTemplatePageData}
                                                style={{
                                                    fontSize: 20,
                                                    float: "right",
                                                    border: "0px",
                                                    cursor: "pointer"
                                                }}
                                            />
                                    }
                                </div>
                            </Col>
                        </Row>
                        <Row className="no-gutter">
                            <Col md={12}>
                                <div className="ag-theme-balham" style={{ height: "85vh", width: "100%" }}>
                                <CygnusGrid
                                    key={gridKey}
                                    fetchData={async () => ({
                                        records: engagementTemplateDetails,
                                        totalCount: engagementTemplateDetails.length,
                                    })}
                                    rowModelType="clientSide"
                                    dataIdentifier="TemplateId"
                                    cachedData={engagementTemplateDetails}
                                    setCachedData={setEngagementTemplateDetails}
                                    columnDefs={templateDetailsColumnDefs}
                                    cachedIsLoading={refreshGrid}
                                    onLoadingChange={setRefreshGrid}
                                    rowSelection="single"
                                    onSelectionChange={onSelectionChanged}
                                />
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
            </>
        </>
    );
};

export default MyTemplatesPage;
